import { useEffect } from 'react';
import { Input, useLocale } from 'shared-ui';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { Form } from 'ui/form/Form';
import { ReportModal } from '../reportModal/ReportModal';
import { OrganizationsTreeSelect } from 'ui/organizationsTreeSelect/OrganizationsTreeSelect';
import { AppMessages } from 'i18n/messages';
import { ReactComponent as ReportCardsIcon } from 'assets/icons/report-cards.svg';
import { ReportTile } from '../reportTile/ReportTile';
import * as Styled from '../Reports.styles';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { ReportErrorAlert } from '../reportErrorAlert';

import { IssuedCardsReportForm, IssuedCardsReportProps } from './IssuedCardsReport.types';

export const IssuedCardsReport = ({
  isModalOpen,
  onModalClose,
  onModalOpen,
  onDownload,
  isLoadingDownload,
  hasNoDataError,
  onEmailModalOpen,
  isEmailModalOpen,
  isSmtpConfigured,
  onResetError,
}: IssuedCardsReportProps) => {
  const { formatMessage } = useLocale();
  const { perms } = usePermissions();

  const { control, watch } = useFormContext<IssuedCardsReportForm>();

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (hasNoDataError && name) {
        onResetError();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, hasNoDataError, onResetError]);

  return (
    <>
      {perms?.RAPORT_WYDANYCH_KART?.view && (
        <ReportTile
          icon={<ReportCardsIcon />}
          name={formatMessage({ id: AppMessages['reports.issuedCardsReport'] })}
          onClick={onModalOpen}
        />
      )}

      <ReportModal
        header={formatMessage({ id: AppMessages['reports.issuedCardsReport'] })}
        onClose={onModalClose}
        open={isModalOpen}
        onSend={onEmailModalOpen}
        onDownload={() => onDownload()}
        isLoading={isLoadingDownload}
        isSendButtonDisabled={isLoadingDownload || !isSmtpConfigured}
        isDownloadButtonDisabled={isLoadingDownload}
      >
        <Form.Grid gridTemplateColumns="1fr 1fr">
          <Controller
            name="nazwisko"
            control={control}
            render={({ field }) => (
              <Input
                label={formatMessage({ id: AppMessages['reports.permissionsSummary.lastName'] })}
                placeholder={formatMessage({ id: AppMessages['reports.permissionsSummary.lastName.placeholder'] })}
                {...field}
              />
            )}
          />

          <Controller
            name="numerKarty"
            control={control}
            render={({ field }) => (
              <Input
                label={formatMessage({ id: AppMessages['reports.permissionsSummary.cardNumber'] })}
                placeholder={formatMessage({ id: AppMessages['reports.permissionsSummary.cardNumber.placeholder'] })}
                {...field}
              />
            )}
          />

          <Controller
            name="blocked"
            control={control}
            render={({ field }) => (
              <FormControl>
                <Styled.RadioGroupLabel>
                  {formatMessage({ id: AppMessages['reports.permissionsSummary.blocked'] })}
                </Styled.RadioGroupLabel>
                <RadioGroup row value={field.value} onChange={field.onChange} name="blocked">
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label={formatMessage({ id: AppMessages['common.yes'] }).toUpperCase()}
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label={formatMessage({ id: AppMessages['common.no'] }).toUpperCase()}
                  />
                </RadioGroup>
              </FormControl>
            )}
          />

          <Styled.FieldContainer>
            <Controller
              name="komorka"
              control={control}
              render={({ field }) => (
                <OrganizationsTreeSelect
                  {...field}
                  multiple={false}
                  placeholder={formatMessage({ id: AppMessages['reports.permissionsSummary.cell.placeholder'] })}
                  label={formatMessage({ id: AppMessages['reports.permissionsSummary.cell'] })}
                />
              )}
            />
          </Styled.FieldContainer>
        </Form.Grid>

        {hasNoDataError && !isEmailModalOpen && (
          <ReportErrorAlert message={formatMessage({ id: AppMessages['reports.error.noData'] })} />
        )}
      </ReportModal>
    </>
  );
};
